import { FlaskConical } from 'lucide-react'
import { GRACKLE_COLORS } from '@/lib/theme'
import { WorksheetScreen } from '@/components/training/WorksheetScreen'
import type { SheetColumn } from '@/components/training/SheetTable'
import type { TrainingWorksheetRow, TrainingVocabularies } from '@/types'

/**
 * The "GracklePDF Online Beta" tab — an access funnel.
 *
 * In the source: 90 invited, 29 responded, 23 demoed, 20 ready, 19 granted. The
 * three stage columns are the funnel, so they are tinted on the last one: what
 * matters when scanning this list is who is still waiting for access.
 *
 * The stages arrive as 1/0 in the sheet rather than TRUE/FALSE, and are stored as
 * text for the same reason the worklist's yes/no columns are — a blank means nobody
 * has said yet, which is a third state and the majority of the tab.
 */

const ACCESS_TINTS: Record<string, string> = {
  Granted: GRACKLE_COLORS.pistachio,
  'Not yet': GRACKLE_COLORS.paleGrey,
}

const columns = (vocab?: TrainingVocabularies): SheetColumn[] => {
  const yesNo = vocab?.yes_no ?? []
  return [
    { key: 'account_name', label: 'CRM account', kind: 'readonly', width: 'min-w-[15rem]' },
    { key: 'company_name', label: 'Company', kind: 'text', width: 'min-w-[15rem]' },
    { key: 'contact_name', label: 'Contact', kind: 'text', width: 'min-w-[11rem]' },
    { key: 'contact_email', label: 'Contact email', kind: 'text', width: 'min-w-[14rem]' },
    {
      // Prose, not a dropdown. This tab is the only one with NO data validation at
      // all, and the column proves why: of 29 rows, 8 read "Active", 3 "TBD", and the
      // rest are sentences — "Demo booked for July 22nd", "Needs to do Grackle PDF
      // onboarding first". An earlier version offered a four-value invented list.
      key: 'response',
      label: 'Response',
      kind: 'longtext',
      width: 'min-w-[18rem]',
    },
    { key: 'rep_name', label: 'Rep', kind: 'text', width: 'min-w-[11rem]' },
    {
      key: 'demo_completed',
      label: 'Demo completed',
      kind: 'select',
      options: yesNo,
      width: 'min-w-[9rem]',
    },
    {
      key: 'ready_for_access',
      label: 'Ready for access',
      kind: 'select',
      options: yesNo,
      width: 'min-w-[9rem]',
    },
    {
      key: 'access_granted',
      label: 'Access granted',
      kind: 'select',
      options: yesNo,
      width: 'min-w-[9rem]',
    },
  ]
}

/** Anything affirmative counts as granted — the sheet used 1, Yes and TRUE. */
const granted = (v: unknown) => ['1', 'yes', 'true'].includes(String(v ?? '').toLowerCase())

export default function TrainingBeta() {
  return (
    <WorksheetScreen
      sheet="beta"
      title="Online Beta"
      description="The GracklePDF Online Beta access funnel: invited, responded, demoed, ready, granted."
      icon={<FlaskConical className="h-6 w-6" aria-hidden="true" />}
      columns={columns}
      tints={ACCESS_TINTS}
      tintFor={(r: TrainingWorksheetRow) =>
        granted(r.access_granted) ? ACCESS_TINTS.Granted : ACCESS_TINTS['Not yet']
      }
      rowLabel={(r) => String(r.company_name ?? r.account_name ?? 'this company')}
      caption="Beta access funnel by company. Rows where access has been granted are tinted; the colour key is below the table."
      searchPlaceholder="Search company, contact or response..."
    />
  )
}
